import React, { useState, useContext, useRef } from 'react';
import AuthContext from './context/AuthContext';
import {
    TextField, Button, Box, Typography, Container, CircularProgress,
    Collapse, Snackbar, Alert, Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { glass } from './theme';

function Login() {
    const { loginUser } = useContext(AuthContext);
    const phoneInputRef = useRef(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setSnackbar({ open: false, message: '', severity: 'success' });
        try {
            const response = await apiClient.post('/api/auth/otp/request/', { phone_number: phoneNumber });
            if (response.status === 200) {
                setOtpSent(true);
                setMessage('کد تایید به شماره شما ارسال شد.');
            } else {
                setMessage('خطا در درخواست کد تایید.');
            }
        } catch (error) {
            setMessage(error.response?.data?.detail || 'خطا در ارسال کد. شماره را بررسی کنید.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setSnackbar({ open: false, message: '', severity: 'success' });
        apiClient.post('/api/auth/otp/verify/', {
            phone_number: phoneNumber,
            otp_code: otpCode,
            user_type: 'requester', // فقط برای ثبت‌نام یک شماره‌ی کاملاً جدید استفاده می‌شود
        })
            .then(response => {
                if (response.data?.tokens?.access) {
                    loginUser(response.data.tokens);
                    setSnackbar({ open: true, message: 'ورود با موفقیت انجام شد!', severity: 'success' });
                } else {
                    setMessage('خطای ورود: اطلاعات لازم از سرور دریافت نشد.');
                    setLoading(false);
                }
            })
            .catch(error => {
                const detail = error.response?.data?.detail;
                if (detail && detail.includes("Invalid or expired OTP")) {
                    setMessage("کد تایید اشتباه یا منقضی شده است.");
                } else if (detail) {
                    setMessage(detail);
                } else {
                    setMessage('خطا در تایید کد یا ورود.');
                }
                setLoading(false);
            });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') { return; }
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', display: 'flex', alignItems: 'center', py: 6 }}>
            <Container component="main" maxWidth="xs">
                <Box sx={{ ...glass.card, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                    <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
                        ورود به الو یارا
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                        چه خانواده باشید و چه همیار، با شماره‌ی موبایل خود وارد شوید
                    </Typography>

                    <Box component="form" onSubmit={otpSent ? handleVerifyOtp : handleRequestOtp} sx={{ mt: 3, width: '100%' }}>
                        <TextField
                            inputRef={phoneInputRef}
                            margin="normal" required fullWidth id="phone_number"
                            label="شماره موبایل (مثال: 09123456789)" name="phone_number"
                            autoComplete="tel" autoFocus value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            disabled={otpSent || loading}
                            inputProps={{ style: { textAlign: 'center' } }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                        />

                        <Collapse in={otpSent} timeout="auto" unmountOnExit>
                            <TextField
                                margin="normal" required={otpSent} fullWidth name="otp_code"
                                label="کد تایید ۶ رقمی" type="number" id="otp_code"
                                value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                                disabled={loading}
                                inputProps={{ style: { textAlign: 'center', letterSpacing: '0.5em' } }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                            />
                        </Collapse>

                        {message && <Typography color={message.includes('خطا') || message.includes('اشتباه') ? 'error' : 'info'} sx={{ mt: 2, textAlign: 'center' }}>{message}</Typography>}

                        <Button type="submit" fullWidth variant="contained" size="large" sx={{ mt: 3, mb: 1 }} disabled={loading}>
                            {loading ? <CircularProgress size={24} color="inherit" /> : (otpSent ? 'تایید و ورود' : 'ارسال کد تایید')}
                        </Button>
                    </Box>

                    {!otpSent && (
                        <>
                            <Divider sx={{ width: '100%', my: 3 }}>یا</Divider>
                            <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                                <Button
                                    fullWidth variant="outlined" color="primary"
                                    onClick={() => phoneInputRef.current?.focus()}
                                    sx={{ flexDirection: 'column', py: 1.5, gap: 0.5 }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>ثبت درخواست مراقبت</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>با شماره‌ی بالا وارد شوید</Typography>
                                </Button>
                                <Button
                                    fullWidth variant="outlined" color="secondary" component={RouterLink} to="/register"
                                    startIcon={<HandshakeIcon fontSize="small" />}
                                    sx={{ flexDirection: 'column', py: 1.5, gap: 0.5, '& .MuiButton-startIcon': { m: 0 } }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>ثبت‌نام همیار</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>احراز هویت + سابقه</Typography>
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}

export default Login;
